import { useEffect, useState } from "react";
import { Plus, Trash2, Gift } from "lucide-react";
import { toast } from "sonner";
import { addProfile, removeProfile, setChildVoice, errorText, getActiveProfileId } from "@/lib/family";
import { getAcorns, VOICES, speak, setProfileVoice } from "@/lib/api";
import { AcornJar } from "@/components/AcornJar";
import { getRewards, useRewardsChanged } from "@/lib/rewards";

const ChildJar = ({ id }) => {
  const [jar, setJar] = useState(null);
  const load = () => getAcorns(id).then(setJar);
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  useRewardsChanged(load);
  return jar ? <AcornJar jar={jar} testId={`child-jar-${id}`} /> : null;
};

const ReadyBadge = ({ id }) => {
  const [ready, setReady] = useState([]);
  const load = () => getRewards(id).then((d) => setReady(d.rewards.filter((r) => d.saved.acorns >= r.acorns && d.saved.golden >= r.golden)));
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  useRewardsChanged(load);
  if (!ready.length) return null;
  return (
    <span className="pp-ready-badge" title={ready.map((r) => r.title).join(", ")} data-testid={`child-ready-badge-${id}`}>
      <Gift className="h-3.5 w-3.5" /> Ready for {ready.length > 1 ? `${ready.length} rewards` : "a reward"}: {ready[0].title}{ready.length > 1 ? "…" : ""}
    </span>
  );
};

const ChildVoice = ({ pin, p, reload }) => {
  const change = async (e) => {
    const voice = e.target.value || null;
    try {
      await setChildVoice(pin, p.id, voice);
      if (getActiveProfileId() === p.id) setProfileVoice(voice);
      if (voice) speak(`Hi ${p.name}! This is your new voice.`, voice);
      reload();
    } catch (err) { toast.error(errorText(err)); }
  };
  return (
    <select className="pp-voice-select" value={p.voice || ""} onChange={change} aria-label={`${p.name}'s voice`} data-testid={`child-voice-${p.id}`}>
      <option value="">Voice: device default</option>
      {VOICES.map(([id, label]) => <option key={id} value={id}>Voice: {label}</option>)}
    </select>
  );
};

export const ChildrenManager = ({ pin, profiles, reload }) => {
  const [name, setName] = useState("");

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      await addProfile(pin, name.trim(), "kid");
      setName("");
      toast.success(`${name.trim()} added`);
      reload();
    } catch (err) { toast.error(errorText(err)); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Remove ${p.name} and their history?`)) return;
    try { await removeProfile(pin, p.id); reload(); } catch (err) { toast.error(errorText(err)); }
  };

  return (
    <section className="pp-card" data-testid="children-manager">
      <h2 className="pp-h2">Children</h2>
      <ul className="mt-4 space-y-2">
        {profiles.length === 0 && <li className="pp-sub" data-testid="no-children">No children yet. Add one below.</li>}
        {profiles.map((p) => (
          <li key={p.id} className="pp-child-row" data-testid={`child-row-${p.id}`}>
            <span className="pp-avatar">{p.name[0].toUpperCase()}</span>
            <span className="font-semibold">{p.name}</span>
            <span className="pp-badge text-xs">Kid 4–12</span>
            <ReadyBadge id={p.id} />
            <ChildVoice pin={pin} p={p} reload={reload} />
            <span className="ml-auto"><ChildJar id={p.id} /></span>
            <button className="pp-icon" onClick={() => remove(p)} aria-label={`Remove ${p.name}`} data-testid={`remove-child-${p.id}`}>
              <Trash2 className="h-4 w-4" />
            </button>
          </li>
        ))}
      </ul>
      <form onSubmit={add} className="pp-add-child" data-testid="add-child-form">
        <input className="pp-input" placeholder="Child's name" value={name} maxLength={30}
          onChange={(e) => setName(e.target.value)} data-testid="child-name-input" />
        <button className="pp-primary" data-testid="add-child-button"><Plus className="h-5 w-5" /> Add</button>
      </form>
    </section>
  );
};
